import { useTranslation } from "react-i18next";

import { PassengerIcon, SuitcaseIcon } from "../../../../components/icons";
import { cn } from "../../../../utils/classnames";
import { createTypographyHelper } from "../../../../utils/typography";

export const Specification = ({ id, passenger, suitcase, length, width, height, seatImage, usesNewLayout }) => {
  const { t, i18n } = useTranslation();
  const getTypographyClass = createTypographyHelper(i18n.language);
  // The ①②③ numbers point at areas on the seat map, so without a map they mean nothing.
  const hasSeatMap = Boolean(seatImage);
  // A narrower label column on phones so "Up to 6 passengers" stays on one line.
  const labelWidthClass = usesNewLayout ? "tw:w-[165px] tw:lg:w-[195px] tw:shrink-0" : "tw:w-[195px]";

  return (
    <>
      <div className="tw:py-8 tw:border-t tw:border-t-[#F1F1F1] tw:flex tw:items-start tw:mt-8">
        <div className={cn(labelWidthClass, "tw:text-[#8D8D8D] tw:font-medium")}>
          {t("Capacity")}
        </div>
        <div className="tw:space-y-[10px]">
          <div className="tw:flex tw:items-center tw:gap-x-[6px]">
            <div>
              <PassengerIcon />
            </div>
            <span
              className={cn(
                "tw:text-[#282828]",
                getTypographyClass(14)
              )}
            >
              {t("Specification.UpToPassengers", { count: passenger })}
            </span>
          </div>
          <div className="tw:flex tw:items-center tw:gap-x-[6px]">
            <div>
              <SuitcaseIcon />
            </div>
            <span
              className={cn(
                "tw:text-[#282828]",
                getTypographyClass(14)
              )}
            >
              {t("Specification.UpToSuitcases", { count: suitcase })}
            </span>
          </div>
        </div>
      </div>
      <div className="tw:py-8 tw:border-t tw:border-t-[#F1F1F1] tw:flex tw:items-start">
        <div className={cn(labelWidthClass, "tw:text-[#8D8D8D] tw:font-medium tw:capitalize")}>
          {t("Interior.Space")}
        </div>
        {
          id === "ToyotaHiAceLuxuryCabin" ? (
            <div className="tw:space-y-[10px]">
              <div className="tw:flex tw:items-center tw:gap-x-[6px]">
                <div
                  className={cn(
                    "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                    getTypographyClass(14)
                  )}
                >
                  <span className="tw:font-sans tw:text-base">&#9312;</span>{" "}
                  <span>{t("Drivers.Seat")}</span>
                </div>
              </div>
              <div className="tw:flex tw:items-center tw:gap-x-[6px]">
                <div
                  className={cn(
                    "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                    getTypographyClass(14)
                  )}
                >
                  <span className="tw:font-sans tw:text-base">&#9313;</span>{" "}
                  <span>{t("Foldable.Seat")}</span>
                </div>
              </div>
              <div className="tw:flex tw:items-center tw:gap-x-[6px]">
                <div
                  className={cn(
                    "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                    getTypographyClass(14)
                  )}
                >
                  <span className="tw:font-sans tw:text-base">&#9314;</span>{" "}
                  <span>{t("Passenger.Area")}</span>
                </div>
              </div>
              <div className="tw:flex tw:items-center tw:gap-x-[6px]">
                <div
                  className={cn(
                    "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                    getTypographyClass(14)
                  )}
                >
                  <span className="tw:font-sans tw:text-base">&#9315;</span>{" "}
                  <span>{t("Luggage.Area")}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="tw:space-y-[10px]">
              <div className="tw:flex tw:items-center tw:gap-x-[6px]">
                <div
                  className={cn(
                    "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                    getTypographyClass(14)
                  )}
                >
                  {hasSeatMap && <span className="tw:font-sans tw:text-base">&#9312;</span>}{" "}
                  <span>{t("Drivers.Seat")}</span>
                </div>
              </div>
              <div className="tw:flex tw:items-center tw:gap-x-[6px]">
                <div
                  className={cn(
                    "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                    getTypographyClass(14)
                  )}
                >
                  {hasSeatMap && <span className="tw:font-sans tw:text-base">&#9313;</span>}{" "}
                  <span>{t("Passenger.Area")}</span>
                </div>
              </div>
              <div className="tw:flex tw:items-center tw:gap-x-[6px]">
                <div
                  className={cn(
                    "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                    getTypographyClass(14)
                  )}
                >
                  {hasSeatMap && <span className="tw:font-sans tw:text-base">&#9314;</span>}{" "}
                  <span>{t("Luggage.Area")}</span>
                </div>
              </div>
            </div>
          )
        }
      </div>
      <div className="tw:py-8 tw:border-t tw:border-t-[#F1F1F1] tw:flex tw:items-start">
        <div className={cn(labelWidthClass, "tw:text-[#8D8D8D] tw:font-medium")}>
          {t("Size.Specifications")}
        </div>
        <div className="tw:space-y-[10px]">
          <div className="tw:flex tw:items-center tw:gap-x-[6px]">
            <div
              className={cn(
                "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                getTypographyClass(14)
              )}
            >
              {t("Length")}: {length.toLocaleString()}mm
            </div>
          </div>
          <div className="tw:flex tw:items-center tw:gap-x-[6px]">
            <div
              className={cn(
                "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                getTypographyClass(14)
              )}
            >
              {t("Width")}: {width.toLocaleString()}mm
            </div>
          </div>
          <div className="tw:flex tw:items-center tw:gap-x-[6px]">
            <div
              className={cn(
                "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                getTypographyClass(14)
              )}
            >
              {t("Height")}: {height.toLocaleString()}mm
            </div>
          </div>
        </div>
      </div>
    </>
  )
}